import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import _ from "lodash";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import {
  selectCartItems,
  selectCartOpen,
  selectCartTotal,
  selectIsCartItemAdding,
  selectIsCartFetching,
  selectIsCartItemClearing,
} from "../../redux/cart/cart.selectors";

import { selectCurrentUser } from "../../redux/user/user.selectors";

import { cartActions } from "../../redux/cart/cart.reducer";

import { clearItem } from "../../redux/cart/cart.utils";

import Drawer from "@material-ui/core/Drawer";
import useStyles from "./cart.styles";

import Price from "../price";
import Loading from "../loading";
import CloseIcon from "../../assets/icons/close-icon";

const Image = ({ image }: { image: any }) => {
  const classes = useStyles({
    image,
  });

  return image ? (
    <img className={classes.image} />
  ) : (
    <div className={classes.noImage}>No Image</div>
  );
};

const CartItem = (props: any) => {
  const { cartItem, clearItemFromCart } = props;

  const isClearing = useSelector(selectIsCartItemClearing);

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!isClearing) {
      setProcessing(false);
    }
  }, [isClearing]);

  const classes = useStyles({
    image: cartItem?.defaultImage,
    processing,
  });

  return (
    <div className={classes.cartItem}>
      <div className={classes.loadingContainer}>
        <Loading on={processing} marginTop="auto" marginBottom="auto" />
      </div>
      <div className={classes.innerCartItem}>
        <Image image={cartItem?.defaultImage} />
        <div className={classes.cartInfo}>
          <div className={classes.brand}>{cartItem?.brand}</div>
          <div className={classes.name}>{cartItem?.title}</div>
          {cartItem?.variantOptionsUsed &&
          cartItem?.variantOptionsUsed.variantOptions ? (
            <div className={classes.option}>
              {_.sortBy(
                Object.keys(cartItem?.variantOptionsUsed?.variantOptions),
                [(o) => o],
              ).map((key: any) => (
                <span key={key}>
                  {key}: {cartItem?.variantOptionsUsed?.variantOptions[key]}
                </span>
              ))}
            </div>
          ) : null}
          <Price item={cartItem} />
          <div className={classes.quantity}>Quantity: {cartItem?.quantity}</div>
          <button
            className={classes.removeBtn}
            onClick={() => {
              clearItemFromCart(cartItem);
              setProcessing(true);
            }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

const Cart = () => {
  const dispatch = useDispatch();
  const isCartOpen = useSelector(selectCartOpen);
  const cartItems = useSelector(selectCartItems);

  const isFetching = useSelector(selectIsCartFetching);
  const isAdding = useSelector(selectIsCartItemAdding);
  const isClearing = useSelector(selectIsCartItemClearing);

  const totalPrice = useSelector(selectCartTotal);

  const currentUser = useSelector(selectCurrentUser);

  const [open, setOpen] = useState(isCartOpen);
  const [loading, setLoading] = useState(true);

  const matches = useMediaQuery("(min-width:1200px)");

  const classes = useStyles({
    isOnSale: (item: any) => item.isOnSale,
    matches,
  });

  useEffect(() => {
    setLoading(isFetching || isAdding);
  }, [isFetching, isAdding]);

  useEffect(() => {
    setOpen(isCartOpen);
    if (currentUser) {
      fetchCart();
    }
  }, [isCartOpen]);

  const fetchCart = () => {
    dispatch({
      type: cartActions.fetchCartStart.type,
      payload: currentUser?.nickname,
    });
  };

  const toggleCart = () => {
    dispatch({
      type: cartActions.toggleCartOpen.type,
    });
  };

  const closeCart = () => {
    setOpen(false);
    if (isCartOpen) {
      toggleCart();
    }
  };

  const clearItemFromCart = (item: any) => {
    clearItem(dispatch, currentUser, item);
  };

  const CartList = () => (
    <div className={classes.cart} role="presentation">
      {loading ? (
        <Loading on marginTop="auto" marginBottom="auto" />
      ) : (
        <div className={classes.cartItems}>
          {cartItems && cartItems.length !== 0 ? (
            _.orderBy(
              [...cartItems],
              ["title", "priceSalesNow", "priceNow", "cartItemId"],
              ["asc", "desc", "desc", "asc"],
            ).map((cartItem: any, index: number) => (
              <CartItem
                key={cartItem?.cartItemId || index}
                cartItem={cartItem}
                clearItemFromCart={clearItemFromCart}
              />
            ))
          ) : (
            <div>Cart is Empty</div>
          )}
        </div>
      )}
      <div className={classes.lowerSection}>
        <div className={classes.total}>
          <span className={classes.totalText}>Estimated Total</span>
          {isClearing || loading ? (
            <Loading on size="1rem" marginRight="2rem" />
          ) : (
            <span>CAD ${totalPrice?.toFixed(2)}</span>
          )}
        </div>
        <Link
          to="/checkout"
          onClick={closeCart}
          className={classes.processToCheckoutBtn}
        >
          Process to Checkout
        </Link>
      </div>
      <button className={classes.closeBtn} onClick={closeCart}>
        <CloseIcon width="30" height="30" />
      </button>
    </div>
  );

  return (
    <Drawer anchor="right" open={open} onClose={closeCart}>
      {CartList()}
    </Drawer>
  );
};

export default Cart;
